import { featurePages } from "./web-features.js";
import { hardware, industries, themesPage } from "./web-solutions.js";
/** The public marketing site (apps/web, docs/specs/marketing-site.md). `{{brand}}` is the configured product name. */
export const web = {
    meta: {
        title: "{{brand}} — online and in-store sales, one stock",
        description: "Run Facebook orders, your shop counter, couriers and every branch from one system with one stock. The POS keeps selling when the internet drops. Built for Bangladesh.",
    },
    skipToContent: "Skip to content",
    nav: {
        label: "Main",
        features: "Features",
        themes: "Themes",
        pricing: "Pricing",
        enterprise: "Enterprise",
        demo: "Demo",
        signIn: "Sign in",
        startTrial: "Start free trial",
        tryIt: "Try it yourself",
        openMenu: "Open menu",
        closeMenu: "Close menu",
        home: "{{brand}} home",
    },
    language: {
        label: "Language",
        switchTo: "বাংলা",
        switchToLabel: "বাংলায় দেখুন",
    },
    hero: {
        eyebrow: "For shops and online sellers in Bangladesh",
        title: "Online and in-store — one system, one stock.",
        body: "Your Facebook orders, your shop counter and every branch from one place. And the POS keeps selling even when the internet doesn't.",
        points: {
            offline: "Works offline",
            couriers: "Steadfast · Pathao · RedX",
            bengali: "English and বাংলা",
        },
    },
    home: {
        heroShotAlt: "The {{brand}} dashboard with a week of sales by day and by channel",
        heroPhoneAlt: "A shop's own website built with {{brand}}, on a phone",
        integrations: {
            title: "Works with what Bangladesh uses",
            payments: "Payments",
            couriers: "Couriers",
        },
        problems: {
            eyebrow: "Why merchants switch",
            title: "Four headaches, one fix",
            body: "Most shops run sales on one tool, stock on a notebook and couriers on three websites. That is where money leaks.",
            before: "Before",
            after: "With {{brand}}",
            items: {
                stock: {
                    before: "Facebook and the shop sell the same last piece — twice.",
                    after: "One stock for every channel. Sell anywhere, the count is right everywhere.",
                },
                offline: {
                    before: "The internet drops and the counter stops billing.",
                    after: "The POS keeps billing offline for up to 7 days and syncs on its own.",
                },
                couriers: {
                    before: "Courier panels, COD sheets and fake orders in separate places.",
                    after: "Book Steadfast, Pathao or RedX in one click, match COD, catch fake orders.",
                },
                branches: {
                    before: "No one knows what each branch sold or holds today.",
                    after: "Every branch, warehouse and counter on one dashboard, live.",
                },
            },
        },
        branches: {
            eyebrow: "Stock & branches",
            title: "Every branch, one stock",
            body: "See what each branch and warehouse holds right now, move stock between them, and count it without closing the shop.",
            points: {
                live: "Live stock per branch, warehouse and counter",
                transfers: "Transfers with send and receive steps",
                counts: "Stock counts and adjustments with a full history",
                purchasing: "Purchase orders, goods receipts and supplier payables",
            },
            shotAlt: "Stock of every product across branches in {{brand}}",
        },
        website: {
            eyebrow: "Your own website",
            title: "A shop website that sells from the same stock",
            body: "Pick one of 12 designs made for Bangladeshi shops, add your logo and colours, and take orders with cash on delivery, bKash or Nagad.",
            browse: "See all themes",
            shotAlt: "The {{name}} theme",
            themes: {
                fashion_women: "Women's fashion",
                fashion_men: "Menswear",
                cosmetics: "Beauty",
                gadgets: "Gadgets",
                grocery: "Grocery",
                home_kitchen: "Home & kitchen",
                kids_baby: "Kids & baby",
                jewellery: "Jewellery",
                home_food: "Home food",
                books: "Bookshop",
                single_product: "Single product",
                minimal: "Minimal",
            },
        },
        accounts: {
            eyebrow: "Accounts & reports",
            title: "Know what you earned today",
            body: "Sales, profit and cash from every channel add up on their own. No more evening sums on a calculator.",
            points: {
                profit: "Profit and loss, by day, branch or channel",
                cod: "COD collected by couriers, matched to orders",
                vat: "VAT invoices and a VAT report",
                cash: "Cashbook, expenses and payables",
                export: "Every report exports to Excel",
            },
            shotAlt: "A profit and loss report in {{brand}}",
        },
        bengali: {
            eyebrow: "English and বাংলা",
            title: "Your staff can work in Bengali",
            body: "Every person picks their own language. Receipts, invoices and SMS to customers can be in Bengali too, with Bengali numerals.",
            english: "English",
            bangla: "বাংলা",
            shotAlt: "The orders screen in {{language}}",
        },
        start: {
            eyebrow: "Getting started",
            title: "Selling in 15 minutes",
            body: "No installer visit, no long setup. Most shops make their first sale the same day.",
            steps: {
                signup: { title: "Sign up", body: "Your phone number and business name. No payment." },
                import: { title: "Add products", body: "Type them in, or import your Excel sheet at once." },
                pair: { title: "Pair the POS", body: "Install it on the counter PC and enter the pairing code." },
                sell: { title: "Make the first sale", body: "Scan, take cash or bKash, print the receipt." },
            },
        },
        pricing: {
            title: "Simple monthly plans for every size",
            body: "Start free, then pick the plan that fits your shop. Plans grow with your branches and staff.",
            points: {
                trial: "Free trial, no payment needed",
                setup: "No setup fee when you sign up yourself",
                change: "Change plan as you grow",
            },
            cta: "See pricing",
        },
        faq: {
            title: "Questions merchants ask",
            items: {
                offline: {
                    q: "What happens when the internet goes down?",
                    a: "The POS keeps working: it bills, prints and takes payments for up to 7 days without internet. When the connection returns it sends every bill on its own — none are lost or counted twice.",
                },
                safety: {
                    q: "Is my business data safe?",
                    a: "Each business's data is kept apart from every other business, passwords and courier keys are encrypted, and the database is backed up every hour.",
                },
                moving: {
                    q: "Can I bring my products from my current system?",
                    a: "Yes. Export them to Excel or CSV and import the file; any row that needs fixing is listed so you can correct it.",
                },
                hardware: {
                    q: "Which hardware does the POS work with?",
                    a: "A Windows or Mac computer, thermal receipt printers, barcode scanners, cash drawers and barcode label printers.",
                },
                bengali: {
                    q: "Can my staff use it in Bengali?",
                    a: "Yes. Each person chooses English or Bengali, and receipts, invoices and SMS can be printed and sent in Bengali.",
                },
                website: {
                    q: "Do I need a website to start?",
                    a: "No. Take orders from Facebook, WhatsApp or the phone first; switch on your own website whenever you are ready.",
                },
            },
        },
        demos: {
            eyebrow: "Try it here",
            note: "A simulation on this page. The real system works the same way.",
            reset: "Start again",
            stock: {
                title: "Sell anywhere. The count stays right.",
                body: "Sell the last saris at the counter or order them on the website — both see the same stock, so the last piece is never sold twice.",
                product: "Jamdani sari, red",
                counter: "Shop counter (POS)",
                website: "Your website",
                sellCounter: "Sell 1 at the counter",
                orderOnline: "Order on the website",
                inStock: "{{count}} in stock",
                soldOut: "Sold out",
                soldOutNote: "Both channels stopped at 0 — nothing was sold twice.",
                log: "What happened",
                logCounter: "Counter sale · stock {{from}} → {{to}}",
                logOnline: "Website order · stock {{from}} → {{to}}",
            },
            offline: {
                title: "The internet went. The shop didn't stop.",
                body: "Turn the internet off and keep billing. When it comes back, every bill syncs on its own — none lost, none twice.",
                internet: "Internet",
                on: "On",
                off: "Off",
                newBill: "New bill",
                bill: "Bill {{number}}",
                waiting: "Waiting to sync",
                syncing: "Syncing…",
                synced: "Synced",
                online: "Online — bills reach the office at once",
                offline: "Offline — bills are kept safe on this computer",
                queued: "{{count}} waiting",
                empty: "Make a bill to start.",
            },
            courier: {
                title: "From a Facebook message to a delivered parcel",
                body: "One order, start to finish: check the customer, book the courier, and see the cash on delivery come back.",
                stepOf: "Step {{n}} of 4",
                steps: {
                    order: "New order",
                    check: "Fraud check",
                    book: "Book courier",
                    cod: "COD back",
                },
                source: "From Facebook",
                customer: "Nusrat Jahan",
                area: "Mirpur 10, Dhaka",
                items: "2 items",
                risk: "Low risk",
                history: "This number: {{delivered}} delivered, {{returned}} returned · {{rate}} success",
                choose: "Choose a courier",
                booked: "Booked with {{courier}}",
                tracking: "Tracking {{code}}",
                collected: "{{amount}} collected by {{courier}} and matched to this order",
                next: "Next step",
            },
        },
        final: {
            title: "Run your whole shop from one place",
            body: "Start the free trial today, or try a demo shop first — no sign-up needed.",
        },
    },
    demo: {
        metaTitle: "Try it yourself",
        eyebrow: "No sign-up, no card",
        title: "Your own demo shop, ready in seconds",
        body: "Get a real shop with a month of sales, two branches, a website and a POS that runs in your browser. Sell, turn the internet off, take an online order, book a courier — then see the report.",
        points: {
            own: "Yours alone for 24 hours, then deleted",
            real: "The same system shops run on every day",
            nothing: "Nothing is sent to anyone: no SMS, no courier, no payment",
        },
        start: "Start my demo shop",
        starting: "Getting your shop ready…",
        unavailable: "The demo is not open yet. Message us and we will show you the system.",
        errors: {
            busy: "All demo shops are in use right now. Try again in a minute.",
            tooMany: "You have tried enough demo shops for today. Start a free trial to keep going.",
            bot: "We could not check that you are not a robot. Please try again.",
            failed: "Something went wrong. Please try again.",
        },
        fallback: {
            title: "Look around the live demo shops",
            body: "Every design runs as a real shop you can browse, add to the cart and check out — no demo needed.",
        },
    },
    pricing: {
        metaTitle: "Pricing",
        eyebrow: "Pricing",
        title: "Simple plans that grow with your shop",
        body: "Start with a free trial — no payment needed. No setup fee when you sign up yourself.",
        monthly: "Monthly",
        yearly: "Yearly",
        perMonth: "/ month",
        perYear: "/ year",
        save: "Save {{amount}} a year",
        popular: "Most chosen",
        start: "Start free trial",
        unlimited: "Unlimited",
        compareTitle: "Compare every feature",
        compareBody: "Everything each plan includes, side by side.",
        seeAll: "Compare all features",
        feature: "Feature",
        included: "Included",
        notIncluded: "Not included",
        groups: {
            usage: "Limits",
            channels: "Ways to sell",
            stock: "Stock and operations",
            delivery: "Delivery",
            payments: "Payments",
            customers: "Customers",
            accounts_reports: "Accounts and reports",
            team: "Team",
            integrations: "Integrations",
            support: "Support",
        },
        onRequest: "Prices are on their way. Message us and we will tell you today.",
        enterprise: {
            title: "A big chain, or the whole system on your own servers?",
            body: "Dedicated instance, data migration, training and a contract that fits.",
            cta: "Talk to us",
        },
    },
    enterprise: {
        metaTitle: "Enterprise",
        eyebrow: "Enterprise",
        title: "The whole system, for your business alone",
        body: "For chains and brands that need their own servers, their own limits and a team that sets everything up with them.",
        points: {
            dedicated: {
                title: "Your own instance",
                body: "Your data on servers of its own — run by us, or on yours under a licence.",
            },
            migration: {
                title: "Data moved for you",
                body: "Products, stock, customers and balances brought over from your current system.",
            },
            training: {
                title: "Training at your branches",
                body: "Your managers and cashiers trained in Bengali, on site or online.",
            },
            support: {
                title: "Priority support",
                body: "A named contact, fixed response times and help on launch day.",
            },
        },
        formTitle: "Tell us about your business",
        formBody: "We call back within one working day.",
    },
    leadForm: {
        name: "Your name",
        business: "Business name",
        phone: "Mobile number",
        email: "E-mail (optional)",
        branches: "Number of branches",
        message: "What do you need? (optional)",
        send: "Send",
        sending: "Sending…",
        sent: "Thank you! We will call you within one working day.",
        errors: {
            phone: "Enter a Bangladeshi mobile number, like 017XXXXXXXX.",
            required: "Please fill in your name, business and mobile number.",
            tooMany: "We have your messages. Please wait a while, or call us.",
            bot: "We could not check that you are not a robot. Please try again.",
            failed: "Something went wrong. Please try again or message us on WhatsApp.",
        },
    },
    contact: {
        whatsapp: "Chat on WhatsApp",
        call: "Call us",
    },
    footer: {
        tagline: "One system for online orders, in-store sales, branches, couriers and accounts.",
        product: "Product",
        solutions: "For your business",
        company: "Company",
        legal: "Legal",
        links: {
            pos: "POS that works offline",
            onlineOrders: "Online orders & couriers",
            inventory: "Stock & branches",
            storefront: "Your own website",
            accounts: "Accounts & reports",
            ebooks: "eBooks & digital",
            hardware: "Hardware",
            fashion: "Fashion",
            grocery: "Grocery",
            cosmetics: "Cosmetics",
            gadgets: "Gadgets",
            bookshop: "Bookshops",
            about: "About us",
            contact: "Contact",
            terms: "Terms",
            privacy: "Privacy",
            refund: "Refund policy",
        },
        madeIn: "Made in Bangladesh",
        rights: "© {{year}} {{brand}}. All rights reserved.",
    },
    notFound: {
        title: "This page is not here",
        body: "The link may be old, or the page is still being built.",
        home: "Back to the home page",
    },
    featurePages,
    industries,
    themesPage,
    hardware,
};
//# sourceMappingURL=web.js.map